import {
  APPLICATION_KEYS,
  type SignInApplicationKey,
  signInApplicationKeyOf,
} from './application-keys';

export type ApplicationEnvironment = 'local' | 'develop' | 'production';

/**
 * Where each application is served, committed here rather than read from the registry so
 * that no environment needs a url set by hand. null is an
 * application not deployed there.
 *
 * A new host is an edit in two places: this constant and `CORS_ORIGINS` in the gateway
 * pipelines (which needs the owner's confirmation). Every origin must be unique across environments: the gateway
 * names a sign-in's application from the origin it is returning to.
 */
export const APPLICATION_URLS: Record<
  ApplicationEnvironment,
  Record<SignInApplicationKey, string | null>
> = {
  local: {
    [APPLICATION_KEYS.projectManagement]: 'http://localhost:5173',
    [APPLICATION_KEYS.onboarding]: 'http://localhost:5175',
    [APPLICATION_KEYS.syncConsole]: 'http://localhost:5176',
    [APPLICATION_KEYS.training]: 'http://localhost:5177',
    [APPLICATION_KEYS.platform]: 'http://localhost:5178',
  },
  develop: {
    [APPLICATION_KEYS.projectManagement]: 'https://project-management.develop.cyrino.com',
    [APPLICATION_KEYS.onboarding]: 'https://onboarding-ca.develop.cyrino.com',
    [APPLICATION_KEYS.syncConsole]: null,
    [APPLICATION_KEYS.training]: 'https://training.develop.cyrino.com',
    [APPLICATION_KEYS.platform]: 'https://platform.develop.cyrino.com',
  },
  production: {
    [APPLICATION_KEYS.projectManagement]: 'https://project-management.cyrino.com',
    [APPLICATION_KEYS.onboarding]: 'https://onboarding-ca.cyrino.com',
    [APPLICATION_KEYS.syncConsole]: 'https://zoho-sync.cyrino.com',
    [APPLICATION_KEYS.training]: 'https://training.cyrino.com',
    [APPLICATION_KEYS.platform]: 'https://platform.cyrino.com',
  },
};

const IPV4_LOOPBACK = /^127(\.\d{1,3}){3}$/;

/** `localhost` (with or without a trailing dot), any `*.localhost`, 127.0.0.0/8 and the IPv6 loopback. */
export const isLoopbackHostname = (hostname: string): boolean => {
  const host = hostname.toLowerCase().replace(/\.$/, '');

  return (
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    IPV4_LOOPBACK.test(host) ||
    host === '[::1]' ||
    host === '::1'
  );
};

/** An origin as the committed urls spell it: lower case, no trailing dot on the host. */
const normalizedOrigin = (value: string): { origin: string; hostname: string } | null => {
  try {
    const url = new URL(value);
    const hostname = url.hostname.replace(/\.$/, '');
    const port = url.port ? `:${url.port}` : '';

    return { origin: `${url.protocol}//${hostname}${port}`, hostname };
  } catch {
    return null;
  }
};

const ENVIRONMENTS = Object.keys(APPLICATION_URLS) as ApplicationEnvironment[];

const hasOrigin = (environment: ApplicationEnvironment, origin: string): boolean =>
  Object.values(APPLICATION_URLS[environment]).some(
    (url) => url !== null && normalizedOrigin(url)?.origin === origin,
  );

/**
 * Which set of hosts a page belongs to: the environment whose urls contain its exact
 * origin. Never by hostname suffix, so an apex, a staging-style host or an IP is not
 * taken for ours. Only a loopback host that matches nothing (a dev proxy on another
 * port) falls back to `local`; anything else is null, and gets no links.
 */
export const applicationEnvironmentFor = (origin: string): ApplicationEnvironment | null => {
  const page = normalizedOrigin(origin);

  if (!page) return null;

  const match = ENVIRONMENTS.find((environment) => hasOrigin(environment, page.origin));

  if (match) return match;

  return isLoopbackHostname(page.hostname) ? 'local' : null;
};

/** The url of an application as seen from a page at `origin`, or null when it has none there. */
export const applicationUrlFor = (applicationKey: string, origin: string): string | null => {
  const environment = applicationEnvironmentFor(origin);

  if (!environment) return null;

  const urls: Record<string, string | null> = APPLICATION_URLS[environment];

  return Object.hasOwn(urls, applicationKey) ? (urls[applicationKey] ?? null) : null;
};

/** The application an origin belongs to, in any environment: how a validated redirect names its target. */
export const applicationKeyForOrigin = (origin: string): SignInApplicationKey | undefined => {
  const wanted = normalizedOrigin(origin)?.origin;

  if (!wanted) return undefined;

  for (const environment of ENVIRONMENTS) {
    for (const [key, url] of Object.entries(APPLICATION_URLS[environment])) {
      if (url !== null && normalizedOrigin(url)?.origin === wanted) {
        return signInApplicationKeyOf(key);
      }
    }
  }

  return undefined;
};
